import React from 'react';
import { FormProvider, FieldValues, SubmitHandler, useFormContext, useForm, DefaultValues } from 'react-hook-form';
import { Button, Input, InputProps } from '../index';
import './Form.css';

export interface FormInputProps extends Omit<InputProps, 'name'> {
  name: string;
}

export type FormButtonProps = {
  type?: 'button' | 'submit' | 'reset';
  primary?: boolean;
  bordered?: boolean;
  size?: 'content' | 'regular' | 'full';
  disabled?: boolean;
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  className?: string;
};

type FormProps<T extends FieldValues = FieldValues> = {
  onSubmit: SubmitHandler<T>;
  children: React.ReactNode;
  className?: string;
  defaultValues?: DefaultValues<T>;
  resolver?: any;
  resetOnSubmit?: boolean;
};


const FormInput: React.FC<FormInputProps> = ({ 
  name,
  ...inputProps
}) => {
  const { register, formState: { errors } } = useFormContext();
  const error = errors[name];

  return (
    <Input
      {...register(name)}
      {...inputProps}
      error={!!error}
      helperText={error?.message as string}
    />
  );
};

const FormButton: React.FC<FormButtonProps> = ({ 
  type = 'button', 
  primary = true,
  bordered = true,
  size = 'regular', 
  disabled = false, 
  children, 
  onClick,                                                                                              
  className = ''
}) => {
  return (
    <Button
      type={type}
      disabled={disabled}
      onClick={onClick}
      primary={primary}
      bordered={bordered}
      size={size}
      className={className}
    >
      {children}
    </Button>
  );
};

const Form = <T extends FieldValues = FieldValues>({
  onSubmit,
  children,
  className = '',
  defaultValues,
  resolver,
  resetOnSubmit = false
}: FormProps<T>) => {
  const form = useForm<T>({
    defaultValues,
    resolver,
    mode: 'onChange'
  });

  const handleSubmit = async (data: T) => {
    try {
      await onSubmit(data);
      if (resetOnSubmit) {
        form.reset(defaultValues);
      }
    } catch (error) {
      // Don't reset on error - let the parent handle error state
      throw error;
    }
  };

  return (
    <FormProvider {...form}>
      <form 
        onSubmit={form.handleSubmit(handleSubmit)} 
        className={`form ${className}`.trim()}
        noValidate
      >
        {children}
      </form>
    </FormProvider>
  );
};


export { Form, FormInput, FormButton };
export default Form;
